// Function index:
//   mountEncounterWindow(ws, initialCombat, selfCharId?, selfArchetype?) —
//       the cinematic combat overlay: opens on combat.started (or an active
//       bootstrap combat), composes the battle line, and drives the action
//       beats from live events — hp drain + hit-flash/heal-glimmer,
//       active-attacker lunge or arrow flight (ranged), persistent KO slump
//       (revived on heal-back-up), chevron on combat.turn, ~1.5s stillness
//       then close on combat.ended. Minimize → pill (badge-pulses on beats
//       while minimized); ESC minimizes (never closes) while combat runs.
//       Chevron/arrow placement is computed in the .ew-stage coordinate
//       space via getBoundingClientRect, re-run after the overlay unhides
//       so open/restore never measure zeros. Click a living foe figure to
//       target it (.ew-targeted ring; click again to clear); the action bar
//       sends the selection as target_npc_id. selfArchetype feeds the
//       action bar's sixth signature button (its own combat.action.resolved
//       / combat.started listeners live inside mountActionBar). Registers
//       additional ws handlers — the wrapper supports many per type, so the
//       combat banner's own bindings are unaffected. Returns a handle whose
//       sync(combat|null) reconciles the window against a fresh bootstrap
//       after a WS reconnect — events during a drop are gone, so this is the
//       only way a fight joined (or ended) mid-drop reaches the window.
//   EncounterWindowHandle — { sync } returned to the runtime's reconnect path.

import type { CombatSnapshot } from '../api.js';
import type { WS } from '../ws.js';
import type { Archetype } from '../game/archetypes.js';
import { buildCast } from './cast.js';
import { composeEncounter } from './compose.js';
import { venueFromTerrain } from './parts/backdrops.js';
import { mountActionBar, type ActionBarHandle } from './action-bar.js';
import type { EncounterCast } from './types.js';

const CLOSE_STILLNESS_MS = 1500;
const BEAT_MS = 600;

export interface EncounterWindowHandle {
  /** Reconcile against a fresh bootstrap after a reconnect: open a fight
   * that started mid-drop, close one that ended mid-drop, refresh a stale
   * cast otherwise. Minimized stays minimized — the player chose that. */
  sync(combat: CombatSnapshot | null): void;
}

export function mountEncounterWindow(
  ws: WS, initialCombat: CombatSnapshot | null, selfCharId: number | null = null,
  /** The speaker's own archetype — drives the action bar's sixth signature
   * button. Fixed for the character's lifetime, so it is read once here
   * rather than derived per combat from participant snapshots. */
  selfArchetype: Archetype | null = null,
  /** ChatMount.say — powers the action bar's free-text last option. */
  say: ((text: string) => boolean) | null = null,
): EncounterWindowHandle {
  const overlay = document.getElementById('encounter-overlay') as HTMLDivElement;
  const win = document.getElementById('encounter-window') as HTMLDivElement;
  const host = document.getElementById('encounter-stage-host') as HTMLDivElement;
  const minBtn = document.getElementById('encounter-min') as HTMLButtonElement;
  const pill = document.getElementById('encounter-pill') as HTMLButtonElement;

  // Clicked target in the tableau. null = server auto-picks (weakest foe).
  // Validated server-side regardless — this is a preference, not authority.
  let targetNpcId: number | null = null;
  // Clicked ALLY (party figure) — used only by the Item button, to heal a
  // party member. One ring at a time: selecting a foe clears the ally and
  // vice versa, so the tableau never shows two competing targets.
  let targetCharacterId: number | null = null;

  // Built here rather than in index.html so the window stays mountable from a
  // hand-rolled DOM (the tests build their own).
  const actionBar: ActionBarHandle = mountActionBar(win, ws, () => targetNpcId, () => targetCharacterId, selfArchetype, say);

  let cast: EncounterCast | null = null;
  let activeKey: string | null = null;
  let open = false;
  let closeTimer: number | null = null;

  const figureEl = (key: string): HTMLElement | null =>
    host.querySelector(`[data-key="${key}"]`);

  const setTarget = (npcId: number | null): void => {
    host.querySelector('.ew-targeted')?.classList.remove('ew-targeted');
    targetNpcId = npcId;
    if (npcId !== null) {
      setAllyTarget(null);
      figureEl(`npc:${npcId}`)?.classList.add('ew-targeted');
    }
  };

  const setAllyTarget = (characterId: number | null): void => {
    host.querySelector('.ew-ally-targeted')?.classList.remove('ew-ally-targeted');
    targetCharacterId = characterId;
    if (characterId !== null) {
      setTarget(null);
      figureEl(`char:${characterId}`)?.classList.add('ew-ally-targeted');
    }
  };

  // Click a living foe figure to target it; click again to let the server
  // auto-pick. Clicking a party figure selects it as the Item button's ally
  // — downed allies stay clickable on purpose: they are exactly who a potion
  // gets poured into.
  host.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-key]');
    if (!el || !cast) return;
    const fig = cast.figures.find((f) => f.key === el.dataset['key']);
    if (!fig) return;
    const id = Number(fig.key.split(':')[1]);
    if (fig.side === 'party' && fig.kind === 'character') {
      setAllyTarget(targetCharacterId === id ? null : id);
      return;
    }
    // cast is a snapshot; live deaths only stamp the ew-down class. Check both.
    if (fig.side !== 'foe' || fig.kind !== 'npc' || fig.defeated) return;
    if (el.classList.contains('ew-down')) return;
    setTarget(targetNpcId === id ? null : id);
  });

  const stageRect = (): DOMRect | null =>
    (host.querySelector('.ew-stage') as HTMLElement | null)?.getBoundingClientRect() ?? null;

  const oneShot = (el: HTMLElement, cls: string): void => {
    el.classList.remove(cls);
    void el.offsetWidth; // restart the animation
    el.classList.add(cls);
    window.setTimeout(() => el.classList.remove(cls), BEAT_MS);
  };

  const placeChevron = (key: string | null): void => {
    const chevron = host.querySelector('.ew-chevron') as HTMLElement | null;
    if (!chevron) return;
    if (!key) { chevron.hidden = true; return; }
    const fig = figureEl(key);
    if (!fig) { chevron.hidden = true; return; }
    const stage = stageRect();
    const rect = fig.getBoundingClientRect();
    if (!stage || rect.width === 0) { chevron.hidden = true; chevron.dataset.target = key; return; }
    chevron.hidden = false;
    chevron.dataset.target = key;
    chevron.style.left = `${rect.left - stage.left + rect.width / 2 - 7}px`;
    chevron.style.top = `${Math.max(rect.top - stage.top - 18, 4)}px`;
  };

  const render = (combat: CombatSnapshot): void => {
    cast = buildCast(combat);
    const venue = venueFromTerrain(combat.terrain, combat.room_id !== null);
    host.innerHTML = '';
    const stage = composeEncounter(cast, venue);
    stage.classList.add('ew-idle');
    host.appendChild(stage);
    const active = combat.participants.find((p) => p.order_idx === combat.current_turn_idx);
    activeKey = active
      ? (active.kind === 'character' ? `char:${active.character_id}` : `npc:${active.npc_id}`)
      : null;
    placeChevron(activeKey);
  };

  const show = (combat: CombatSnapshot): void => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    setTarget(null); // a rebuild replaces every figure node
    setAllyTarget(null);
    render(combat);
    win.classList.remove('ew-closing');
    overlay.hidden = false;
    pill.hidden = true;
    open = true;
    placeChevron(activeKey); // re-place now that the overlay is unhidden; render() measured while still display:none
    syncBar();
  };

  // Enabled only on this player's own turn, mirroring the chat input gate in
  // game/runtime.ts — a button must never do what typing may not.
  const syncBar = (): void => {
    actionBar.setEnabled(
      open && selfCharId !== null && activeKey === `char:${selfCharId}`,
    );
  };

  const minimize = (): void => {
    if (!open) return;
    overlay.hidden = true;
    pill.hidden = false;
  };

  const restore = (): void => {
    if (!open) return;
    overlay.hidden = false;
    pill.hidden = true;
    pill.classList.remove('badge-pulse');
    placeChevron(activeKey);
  };

  const close = (): void => {
    open = false;
    setTarget(null);
    actionBar.setEnabled(false);
    win.classList.add('ew-closing');
    pill.classList.remove('badge-pulse');
    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      if (open) return;
      overlay.hidden = true;
      pill.hidden = true;
      host.innerHTML = '';
      cast = null;
    }, CLOSE_STILLNESS_MS);
  };

  const updateHp = (key: string, hp: number, maxHp: number, delta: number, defeated: boolean): void => {
    const fig = figureEl(key);
    if (!fig || !open) return;
    const fill = fig.querySelector('.ew-hp-fill') as HTMLElement | null;
    if (fill) fill.style.width = `${maxHp > 0 ? Math.round((hp / maxHp) * 100) : 0}%`;
    if (delta < 0) {
      oneShot(fig, 'ew-hit');
      beatFromActive(key);
    } else if (delta > 0) {
      oneShot(fig, 'ew-heal');
    }
    if (defeated || hp <= 0) {
      fig.classList.add('ew-down');
      // Dead target, dead selection — the ring must never sit on a corpse.
      if (key === `npc:${targetNpcId}`) setTarget(null);
    } else {
      fig.classList.remove('ew-down');
    }
    if (delta !== 0 && overlay.hidden && open) {
      pill.classList.remove('badge-pulse');
      void pill.offsetWidth; // restart the animation
      pill.classList.add('badge-pulse');
    }
  };

  const beatFromActive = (targetKey: string): void => {
    if (!activeKey || activeKey === targetKey || !cast) return;
    const attacker = cast.figures.find((f) => f.key === activeKey);
    const attackerEl = figureEl(activeKey);
    if (!attacker || !attackerEl) return;
    if (attacker.ranged) {
      const stage = host.querySelector('.ew-stage') as HTMLElement;
      const arrow = document.createElement('div');
      arrow.className = 'ew-arrow';
      if (attacker.side === 'foe') arrow.classList.add('ew-arrow-foe');
      const stageBox = stageRect();
      const rect = attackerEl.getBoundingClientRect();
      if (stageBox && rect.width > 0) {
        arrow.style.left = `${rect.left - stageBox.left + rect.width / 2}px`;
        arrow.style.top = `${rect.top - stageBox.top + 40}px`;
      } else {
        arrow.style.left = '0px';
        arrow.style.top = '40px';
      }
      stage.appendChild(arrow);
      window.setTimeout(() => arrow.remove(), BEAT_MS);
    } else {
      oneShot(attackerEl, 'ew-lunge');
    }
  };

  minBtn.addEventListener('click', minimize);
  pill.addEventListener('click', restore);
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open && !overlay.hidden) minimize();
  });

  ws.on('combat.started', (msg: CombatSnapshot) => show(msg));
  ws.on('combat.turn', (msg: { current_turn_idx: number; active: { kind: string; character_id: number | null; npc_id: number | null } }) => {
    if (!open) return;
    activeKey = msg.active.kind === 'character' ? `char:${msg.active.character_id}` : `npc:${msg.active.npc_id}`;
    placeChevron(activeKey);
    syncBar();
  });
  ws.on('character.hp_changed', (msg: { character_id: number; hp: number; max_hp: number; delta: number }) => {
    updateHp(`char:${msg.character_id}`, msg.hp, msg.max_hp, msg.delta, msg.hp <= 0);
  });
  ws.on('npc.hp_changed', (msg: { npc_id: number; hp: number; max_hp: number; delta: number; defeated: boolean }) => {
    updateHp(`npc:${msg.npc_id}`, msg.hp, msg.max_hp, msg.delta, msg.defeated);
  });
  // Beats for outcomes that change no HP. Damaging outcomes are deliberately
  // left to updateHp's existing hp-driven beat, so the legacy GM apply_damage
  // path keeps behaving exactly as before.
  ws.on('combat.action.resolved', (msg: { actor_key: string; target_key: string | null; outcome: string }) => {
    if (!open) return;
    const actor = figureEl(msg.actor_key);
    const target = msg.target_key ? figureEl(msg.target_key) : null;
    if (msg.outcome === 'miss' || msg.outcome === 'fumble') {
      // A miss still needs the swing, or the fight looks frozen — the lunge
      // comes from the existing beat logic, the sidestep is the tell.
      if (msg.target_key) beatFromActive(msg.target_key);
      if (target) oneShot(target, 'ew-dodge');
    } else if (msg.outcome === 'defend') {
      if (actor) oneShot(actor, 'ew-guard');
    } else if (msg.outcome === 'dodge' || msg.outcome === 'flee_failed') {
      if (actor) oneShot(actor, 'ew-dodge');
    } else if (msg.outcome === 'flee') {
      if (actor) oneShot(actor, 'ew-fade');
    }
    if (overlay.hidden && !pill.hidden) pill.classList.add('badge-pulse');
    // A resolved action that did NOT advance the turn (the Item button's
    // mercy no-op) must hand the bar straight back — combat.turn will never
    // fire for it. syncBar recomputes from the live state, so this is a
    // no-op when the turn really moved.
    syncBar();
  });

  // Rejections arrive as private system messages ("try again", "no rope") and
  // never advance the turn — without this, the bar stayed dead on the
  // player's own turn under a "waiting…" hint that contradicted the chat.
  ws.on('system.message', () => { if (open) syncBar(); });

  ws.on('combat.ended', () => { if (open) close(); });

  if (initialCombat) show(initialCombat);

  return {
    sync(combat: CombatSnapshot | null): void {
      if (combat) {
        if (open && overlay.hidden) {
          // Minimized: refresh the tableau but honour the player's choice —
          // restore() re-places the chevron when they bring it back.
          render(combat);
          syncBar();
        } else {
          show(combat);
        }
      } else if (open) {
        close();
      }
    },
  };
}
